// src/components/edge-nodes/EdgeNodeDetail.jsx

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Badge from '../common/Badge';
import Loading from '../common/Loading';
import { edgeNodesService } from '../../services/edge-nodes';

const EdgeNodeDetail = () => {
  const { nodeId } = useParams();
  const navigate = useNavigate();
  const [node, setNode] = useState(null);
  const [assignedPolicies, setAssignedPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // New states for policy assignment
  const [availablePolicies, setAvailablePolicies] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningPolicy, setAssigningPolicy] = useState(false);

  useEffect(() => {
    const fetchEdgeNode = async () => {
      try {
        setLoading(true);
        // *** FIX APPLIED: Trim whitespace from the nodeId ***
        const data = await edgeNodesService.getEdgeNode(nodeId.trim());
        setNode(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching edge node details:', err);
        setError('Failed to load edge node details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (nodeId) {
      fetchEdgeNode();
    }
  }, [nodeId]);

  useEffect(() => {
    const fetchAssignedPolicies = async () => {
      try {
        setPoliciesLoading(true);
        // *** FIX APPLIED: Trim whitespace from the nodeId ***
        const data = await edgeNodesService.getAssignedPolicies(nodeId.trim());
        setAssignedPolicies(data);
      } catch (err) {
        console.error('Error fetching assigned policies:', err);
      } finally {
        setPoliciesLoading(false);
      }
    };

    if (nodeId) {
      fetchAssignedPolicies();
    }
  }, [nodeId]);

  const fetchAvailablePolicies = async () => {
    try {
      // Import the policies service
      const { policiesService } = await import('../../services/policies');
      const allPolicies = await policiesService.getPolicies();
      
      // Filter out already assigned policies
      const assignedIds = assignedPolicies.map(p => p.policy_id);
      const available = allPolicies.filter(p => !assignedIds.includes(p.policy_id));
      setAvailablePolicies(available);
    } catch (err) {
      console.error('Error fetching available policies:', err);
    }
  };

  const handleAssignPolicy = async (policyId) => {
    try {
      setAssigningPolicy(true);
      await edgeNodesService.assignPolicy(nodeId.trim(), policyId);
      
      // Refresh the assigned policies list
      const data = await edgeNodesService.getAssignedPolicies(nodeId.trim());
      setAssignedPolicies(data);
      
      setShowAssignModal(false);
      setAssigningPolicy(false);
    } catch (err) {
      console.error('Error assigning policy:', err);
      alert('Failed to assign policy. Please try again.');
      setAssigningPolicy(false);
    }
  };

  const handleUnassignPolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to unassign this policy from this edge node?')) {
      return;
    }
    
    try {
      await edgeNodesService.unassignPolicy(nodeId.trim(), policyId);
      
      // Refresh the assigned policies list
      const data = await edgeNodesService.getAssignedPolicies(nodeId.trim());
      setAssignedPolicies(data);
    } catch (err) {
      console.error('Error unassigning policy:', err);
      alert('Failed to unassign policy. Please try again.');
    }
  };

  const handleOpenAssignModal = async () => {
    setShowAssignModal(true);
    await fetchAvailablePolicies();
  };

  const handleDelete = async () => {
    if (confirmDelete) {
      try {
        // *** FIX APPLIED: Trim whitespace from the nodeId ***
        await edgeNodesService.deleteEdgeNode(nodeId.trim());
        navigate('/edge-nodes');
      } catch (err) {
        console.error('Error deleting edge node:', err);
        setError('Failed to delete edge node. Please try again.');
        setConfirmDelete(false);
      }
    } else {
      setConfirmDelete(true);
    }
  };

  if (loading) {
    return <Loading text="Loading edge node details..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            {/* Error Icon */}
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
            <div className="mt-2">
              <Link to="/edge-nodes" className="text-sm text-red-700 underline">
                Return to edge nodes list
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!node) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">Edge node not found.</p>
        <Link to="/edge-nodes" className="text-blue-600 hover:text-blue-500 mt-4 inline-block">
          Return to edge nodes list
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Edge Node Overview Card */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {node.hostname || 'Unnamed Edge Node'}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              ID: {node.edge_node_id}
            </p>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/edge-nodes/${node.edge_node_id}/edit`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${confirmDelete ? 'bg-red-800 hover:bg-red-900' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {confirmDelete ? 'Confirm Delete' : 'Delete'}
            </button>
          </div>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <Badge type={node.status.toLowerCase()} text={node.status} />
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Firmware Version</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{node.firmware_version || 'Unknown'}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">AI Sentry Agent Version</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{node.ot_sentry_agent_version || 'Unknown'}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Last Heartbeat</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {node.last_heartbeat ? new Date(node.last_heartbeat).toLocaleString() : 'Never'}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Location Information</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {node.location_info && Object.keys(node.location_info).length > 0 ? (
                  <pre className="bg-white p-2 rounded text-xs overflow-auto border border-gray-200">
                    {JSON.stringify(node.location_info, null, 2)}
                  </pre>
                ) : (
                  <span className="text-gray-400">No location information</span>
                )}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Custom Tags</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {node.custom_tags && Object.keys(node.custom_tags).length > 0 ? (
                  <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto border border-gray-200">
                    {JSON.stringify(node.custom_tags, null, 2)}
                  </pre>
                ) : (
                  <span className="text-gray-400">No custom tags</span>
                )}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Registered At</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {node.registered_at ? new Date(node.registered_at).toLocaleString() : 'Unknown'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <Card title="Assigned Security Policies">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {assignedPolicies.length} {assignedPolicies.length === 1 ? 'policy' : 'policies'} assigned
          </p>
          <button
            onClick={handleOpenAssignModal}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Assign Policy
          </button>
        </div>
        
        {policiesLoading ? (
          <div className="text-center py-4">
            <Loading text="Loading assigned policies..." />
          </div>
        ) : assignedPolicies.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No policies assigned to this edge node.</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {assignedPolicies.map((policy) => (
                <li key={policy.policy_id} className="px-4 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Link
                        to={`/policies/${policy.policy_id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        {policy.policy_name}
                      </Link>
                      <p className="mt-1 text-sm text-gray-500">
                        {policy.description}
                      </p>
                      <div className="mt-2 flex items-center space-x-4 text-xs">
                        <Badge 
                          type="info" 
                          text={policy.policy_type}
                        />
                        <Badge 
                          type={policy.is_enabled ? 'success' : 'default'}
                          text={policy.is_enabled ? 'Enabled' : 'Disabled'}
                        />
                        <span className="text-gray-500">
                          {policy.rules?.length || 0} rule{policy.rules?.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnassignPolicy(policy.policy_id)}
                      className="ml-4 inline-flex items-center px-2.5 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Unassign
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* Policy Assignment Modal */}
      {showAssignModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAssignModal(false)}></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Assign Policy to Edge Node
                  </h3>
                  
                  {availablePolicies.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No available policies to assign. All policies are already assigned to this edge node.
                    </p>
                  ) : (
                    <div className="mt-2 max-h-96 overflow-y-auto">
                      <ul className="divide-y divide-gray-200">
                        {availablePolicies.map((policy) => (
                          <li key={policy.policy_id} className="py-3 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {policy.policy_name}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                  {policy.description}
                                </p>
                              </div>
                              <button
                                onClick={() => handleAssignPolicy(policy.policy_id)}
                                disabled={assigningPolicy}
                                className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {assigningPolicy ? 'Assigning...' : 'Assign'}
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <Link
          to="/edge-nodes"
          className="text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          ← Back to edge nodes list
        </Link>
      </div>
    </div>
  );
};

export default EdgeNodeDetail;
